'use client';

import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { featuresData } from '@/data/features';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

const THEME = {
  accentGold: '#C89B3C',
  headingText: '#1F1F1F',
  descriptionText: '#777777',
  borderColor: '#ECECEC',
};

export const FeatureBar: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useGSAP(
    () => {
      if (!containerRef.current) return;

      const cardElements = cardsRef.current.filter(
        (el): el is HTMLDivElement => el !== null
      );

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      });

      timeline.to(containerRef.current, {
        opacity: 1,
        duration: 0.6,
        ease: 'power2.out',
      });

      timeline.fromTo(
        cardElements,
        { y: 35, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.7,
          stagger: 0.12,
          ease: 'power3.out',
        },
        '-=0.3'
      );
    },
    { scope: containerRef }
  );

  return (
    <section 
      aria-label="Key Service Highlights"
      className="w-full bg-white px-4 py-4 sm:py-6 md:py-8 flex justify-center"
    >
      <div
        ref={containerRef}
        className="w-full max-w-[1400px] bg-white border border-[#ECECEC] rounded-lg shadow-xs opacity-0 overflow-hidden"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 w-full">
          {featuresData.map((feature, index) => {
            const Icon = feature.icon;

            return (
              <div
                key={feature.id}
                ref={(el) => {
                  cardsRef.current[index] = el;
                }}
                className={`
                  group flex items-center justify-start gap-4 p-5 md:p-6
                  transition-transform duration-300 ease-out cursor-pointer hover:-translate-y-1
                  border-b sm:border-b-0 border-[#ECECEC] last:border-b-0
                  sm:[&:nth-child(1)]:border-r sm:[&:nth-child(1)]:border-b sm:[&:nth-child(2)]:border-b lg:sm:[&:nth-child(1)]:border-b-0 lg:sm:[&:nth-child(2)]:border-b-0
                  lg:border-r lg:border-[#ECECEC] lg:last:border-r-0
                `}
              >
                <div className="shrink-0 flex items-center justify-center w-12 h-12">
                  <Icon
                    size={42}
                    color={THEME.accentGold}
                    strokeWidth={1.5}
                    className="transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"
                    aria-hidden="true"
                  />
                </div>

                <div className="flex flex-col">
                  <h3
                    className="font-semibold text-[15px] sm:text-[16px] lg:text-[17px] leading-tight transition-colors duration-300 group-hover:text-[#C89B3C]"
                    style={{ color: THEME.headingText }}
                  >
                    {feature.title}
                  </h3>
                  <p
                    className="text-[12px] sm:text-[13px] lg:text-[14px] mt-1 font-normal leading-snug"
                    style={{ color: THEME.descriptionText }}
                  >
                    {feature.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeatureBar;